import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/services/api';
import { getTenantId } from '@/services/tokenStorage';

type ImageCategory = 'products' | 'brands' | 'avatars' | 'expenses' | 'settings' | 'receipts' | 'employees';

const globalCache = new Map<string, { url: string; refCount: number }>();
/**
 * In-flight requests, keyed by cache key. Two concurrent callers that both miss
 * the cache each issued their own request and each createObjectURL overwrote the
 * previous entry — the first object URL leaked and was never revoked. Sharing the
 * pending promise makes concurrent callers one request.
 */
const pendingFetches = new Map<string, Promise<string>>();

function getCacheKey(category: ImageCategory, filename: string): string {
  // Tenant-scoped: the same filename across tenants must not resolve to one
  // shared object URL (cross-tenant image leak).
  return `${getTenantId() ?? 'anon'}:${category}:${filename}`;
}

function isValidFilename(filename: any): filename is string {
  return typeof filename === 'string' && filename.length > 0 && filename !== 'no-image.png' && filename !== 'no_avatar.png';
}

async function fetchImageBlob(category: ImageCategory, filename: string): Promise<string> {
  const cacheKey = getCacheKey(category, filename);

  const cached = globalCache.get(cacheKey);
  if (cached) {
    cached.refCount++;
    return cached.url;
  }

  const inFlight = pendingFetches.get(cacheKey);
  if (inFlight) {
    // Join the request already on the wire; the shared result acquires one
    // reference for this caller too.
    const sharedUrl = await inFlight;
    if (!sharedUrl) return '';
    const entry = globalCache.get(cacheKey);
    if (entry) entry.refCount++;
    return sharedUrl;
  }

  const request = (async (): Promise<string> => {
    try {
      const response = await api.get(`/images/${category}/${filename}`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      globalCache.set(cacheKey, { url: objectUrl, refCount: 1 });
      return objectUrl;
    } catch {
      return '';
    } finally {
      pendingFetches.delete(cacheKey);
    }
  })();

  pendingFetches.set(cacheKey, request);
  return request;
}

function releaseCacheKey(cacheKey: string): void {
  const cached = globalCache.get(cacheKey);
  if (!cached) return;
  cached.refCount--;
  if (cached.refCount <= 0) {
    URL.revokeObjectURL(cached.url);
    globalCache.delete(cacheKey);
  }
}

function revokeImage(category: ImageCategory, filename: string): void {
  releaseCacheKey(getCacheKey(category, filename));
}

export function useTenantImage(category: ImageCategory, filename?: string | null) {
  const [imageUrl, setImageUrl] = useState('');
  const mountedRef = useRef(true);
  // The filename whose globalCache refcount this instance actually acquired —
  // only it may release it (an unconditional unload used to decrement refs
  // held by other consumers, revoking URLs still in use).
  const heldFilenameRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (name?: string | null) => {
    const fn = name ?? filename;
    if (!isValidFilename(fn)) {
      if (heldFilenameRef.current) {
        revokeImage(category, heldFilenameRef.current);
        heldFilenameRef.current = null;
      }
      setImageUrl('');
      return;
    }
    const seq = ++requestSeqRef.current;
    const url = await fetchImageBlob(category, fn);
    // fetchImageBlob only takes a refcount when it returns a URL.
    if (url && (seq !== requestSeqRef.current || !mountedRef.current)) {
      // filename changed or unmounted mid-flight — release the fresh ref and
      // never setState for a discarded request.
      revokeImage(category, fn);
      return;
    }
    if (url && heldFilenameRef.current === fn) {
      // Already holding this filename — drop the duplicate acquire.
      revokeImage(category, fn);
      return;
    }
    if (heldFilenameRef.current) {
      revokeImage(category, heldFilenameRef.current);
    }
    heldFilenameRef.current = url ? fn : null;
    setImageUrl(url);
  }, [category, filename]);

  const unload = useCallback((name?: string | null) => {
    const fn = name ?? filename;
    // Release only refs this instance actually acquired. With an explicit
    // filename it must match what is held (never decrement a refcount owned
    // by another consumer); an unnamed unload (the unmount cleanup, incl.
    // the manual-load pattern) releases whatever is held so manual loads do
    // not pin the URL forever.
    if (heldFilenameRef.current && (!isValidFilename(fn) || heldFilenameRef.current === fn)) {
      revokeImage(category, heldFilenameRef.current);
      heldFilenameRef.current = null;
    }
    setImageUrl('');
  }, [category, filename]);

  useEffect(() => {
    if (isValidFilename(filename)) {
      load(filename);
    }
    return () => unload();
  }, [filename, load, unload]);

  return { imageUrl, setImageUrl, load, unload };
}

export function useTenantImages() {
  const urlMapRef = useRef<Map<string, string>>(new Map());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getImageUrl = useCallback((category: ImageCategory, filename: string | null | undefined): string => {
    if (!isValidFilename(filename)) return '';
    const cacheKey = getCacheKey(category, filename!);
    return urlMapRef.current.get(cacheKey) || '';
  }, []);

  const loadImage = useCallback(async (category: ImageCategory, filename: string | null | undefined): Promise<string> => {
    if (!isValidFilename(filename)) return '';
    const cacheKey = getCacheKey(category, filename!);
    if (urlMapRef.current.has(cacheKey)) return urlMapRef.current.get(cacheKey)!;
    const url = await fetchImageBlob(category, filename!);
    // Unmounted mid-flight: release the fresh ref instead of recording it in
    // a map that will never be revoked again.
    if (!mountedRef.current) {
      if (url) releaseCacheKey(cacheKey);
      return url;
    }
    if (url) urlMapRef.current.set(cacheKey, url);
    return url;
  }, []);

  const revokeAll = useCallback(() => {
    // Release through the shared helper: decrement only the refcounts this
    // instance actually acquired (recorded in urlMapRef), never entries that
    // other consumers (useTenantImage or sibling instances) still hold.
    for (const cacheKey of urlMapRef.current.keys()) {
      releaseCacheKey(cacheKey);
    }
    urlMapRef.current.clear();
  }, []);

  useEffect(() => {
    return () => revokeAll();
  }, [revokeAll]);

  return { getImageUrl, loadImage, revokeAll };
}
